import React from 'react';
import { AlertCircle, RotateCw } from 'lucide-react';

import { Alert, AlertDescription, AlertTitle } from './ui/alert';
import { Button } from './ui/button';
import { UNRETRYABLE_KINDS, describeError } from '../lib/errors';
import { cn } from '../lib/utils';

export interface ErrorStateProps {
  /** Names what failed ("Couldn't load sensors"). Required: there is no generic default. */
  title: string;
  /**
   * The caught value. The body is its cause from describeError, with no
   * context prefix — the title already says what failed.
   */
  error: unknown;
  /**
   * Retries the operation that actually failed — wiring Retry to an unrelated
   * refresh is the anti-pattern this component exists to end.
   */
  onRetry?: () => void;
  retryLabel?: string;
  /**
   * Forces Retry on (true) or off (false). By default Retry is hidden for
   * forbidden, not_found and unauthenticated errors, which a retry can never
   * fix. Pass true only where the retry is not the failed call itself (a
   * retry that signs in again).
   */
  retryable?: boolean;
  /** A retry is running: Retry stays disabled with its spinner and keeps its label. */
  isRetrying?: boolean;
  /** Another way out in the same row, after Retry (Close, Clear search). */
  action?: React.ReactNode;
  /** For errors inside dialogs, panes and pickers: no max width, tighter title and spacing. */
  compact?: boolean;
  className?: string;
}

/** Blocking error surface for failed loads. Submit failures use FormError; transient ones use toasts. */
export const ErrorState: React.FC<ErrorStateProps> = ({
  title,
  error,
  onRetry,
  retryLabel = 'Retry',
  retryable,
  isRetrying = false,
  action,
  compact = false,
  className,
}) => {
  const { kind, message } = describeError(error);
  const showRetry = Boolean(onRetry) && (retryable ?? !UNRETRYABLE_KINDS.includes(kind));

  return (
    <Alert variant="destructive" className={cn(!compact && 'max-w-xl', className)}>
      <AlertCircle aria-hidden="true" />
      <div>
        <AlertTitle className={cn(compact && 'text-sm')}>{title}</AlertTitle>
        <AlertDescription>
          <p>{message}</p>
          {showRetry || action ? (
            <div className={cn('flex flex-wrap items-center gap-2', compact ? 'mt-2' : 'mt-3')}>
              {showRetry ? (
                // type="button": ErrorState renders inside dialog forms (the admin
                // lens and license key editors), where a Retry must not submit.
                <Button type="button" variant="outline" size="sm" loading={isRetrying} onClick={onRetry}>
                  <RotateCw aria-hidden="true" />
                  {retryLabel}
                </Button>
              ) : null}
              {action}
            </div>
          ) : null}
        </AlertDescription>
      </div>
    </Alert>
  );
};
